import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalcIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { calculateMedicalTariffPreview } from '../../services/medicalTransportBillingService';

/**
 * Tab „Tarife" — Pflege der Vergütungsvereinbarungen (§ 133 SGB V).
 * Konzept: nextcab_mobile/docs/general/krankenfahrten_tarife_konzept.md §8.4–8.7
 *
 * Diese Seite ist **optional**. Verbindlich bewertet das Abrechnungszentrum;
 * die hier gepflegten Verträge liefern die Erlösvorschau für die Disposition,
 * den Zuzahlungsbetrag und eine Kontrolle gegen die spätere Abrechnung.
 *
 * Gerechnet wird ausschließlich in der Cloud Function
 * (`calculateMedicalTariffPreview`) — auch der Testrechner unten. Eine zweite
 * Rechenlogik im Portal wäre nach wenigen Vertragsänderungen abgedriftet.
 */

const COLLECTION = 'krankenkassenTarife';

const EMPTY_TARIFF = {
  tarifkennzeichen: '',
  bezeichnung: '',
  verkehrsform: 'taxi',
  gueltigVon: '',
  gueltigBis: '',
  kostentraegerIks: '',
  istStandard: false,
  aktiv: true,
  quelle: '',
  positionen: {
    grundpauschale: '',
    kmStaffeln: [{ vonKm: '0', bisKm: '', proKm: '', pauschale: '' }],
    wartezeit: { freiMinuten: '', proStunde: '' },
    standzeit: { freiMinuten: '', proStunde: '' },
    zuschlaege: {
      rollstuhl: '',
      tragestuhl: '',
      treppenhilfeProEtage: '',
      begleitperson: ''
    },
    mindestpreis: '',
    sammelfahrtRegel: 'none'
  }
};

const asText = (value) => (value === null || value === undefined ? '' : String(value));

const toNumberOrNull = (value) => {
  const text = asText(value).trim().replace(',', '.');
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

const toDateInput = (value) => {
  if (!value) return '';
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const formatEur = (value) => {
  if (value === null || value === undefined) return '–';
  return `${Number(value).toFixed(2).replace('.', ',')} €`;
};

/** Formularwerte (Strings) → Firestore-Dokument (Zahlen, Arrays, Timestamps). */
const toFirestoreTariff = (form) => ({
  tarifkennzeichen: form.tarifkennzeichen.trim(),
  bezeichnung: form.bezeichnung.trim(),
  verkehrsform: form.verkehrsform,
  gueltigVon: form.gueltigVon ? new Date(`${form.gueltigVon}T00:00:00`) : null,
  gueltigBis: form.gueltigBis ? new Date(`${form.gueltigBis}T23:59:59`) : null,
  kostentraegerIks: form.kostentraegerIks
    .split(/[\s,;]+/)
    .map(entry => entry.trim())
    .filter(Boolean),
  istStandard: form.istStandard === true,
  aktiv: form.aktiv !== false,
  quelle: form.quelle.trim(),
  positionen: toFirestorePositions(form.positionen)
});

const toFirestorePositions = (positionen) => ({
  grundpauschale: toNumberOrNull(positionen.grundpauschale),
  kmStaffeln: (positionen.kmStaffeln || [])
    .map(staffel => ({
      vonKm: toNumberOrNull(staffel.vonKm) ?? 0,
      bisKm: toNumberOrNull(staffel.bisKm),
      proKm: toNumberOrNull(staffel.proKm),
      pauschale: toNumberOrNull(staffel.pauschale)
    }))
    // Leere Zeilen (nur „von" ausgefüllt) nicht speichern.
    .filter(staffel => staffel.proKm !== null || staffel.pauschale !== null),
  wartezeit: {
    freiMinuten: toNumberOrNull(positionen.wartezeit.freiMinuten),
    proStunde: toNumberOrNull(positionen.wartezeit.proStunde)
  },
  standzeit: {
    freiMinuten: toNumberOrNull(positionen.standzeit.freiMinuten),
    proStunde: toNumberOrNull(positionen.standzeit.proStunde)
  },
  zuschlaege: Object.fromEntries(
    Object.entries(positionen.zuschlaege).map(([key, value]) => [key, toNumberOrNull(value)])
  ),
  mindestpreis: toNumberOrNull(positionen.mindestpreis),
  sammelfahrtRegel: positionen.sammelfahrtRegel
});

const fromFirestoreTariff = (data) => ({
  tarifkennzeichen: asText(data.tarifkennzeichen),
  bezeichnung: asText(data.bezeichnung),
  verkehrsform: asText(data.verkehrsform) || 'taxi',
  gueltigVon: toDateInput(data.gueltigVon),
  gueltigBis: toDateInput(data.gueltigBis),
  kostentraegerIks: Array.isArray(data.kostentraegerIks) ? data.kostentraegerIks.join(', ') : '',
  istStandard: data.istStandard === true,
  aktiv: data.aktiv !== false,
  quelle: asText(data.quelle),
  positionen: {
    grundpauschale: asText(data.positionen?.grundpauschale),
    kmStaffeln: (Array.isArray(data.positionen?.kmStaffeln) && data.positionen.kmStaffeln.length > 0
      ? data.positionen.kmStaffeln
      : EMPTY_TARIFF.positionen.kmStaffeln
    ).map(staffel => ({
      vonKm: asText(staffel.vonKm),
      bisKm: asText(staffel.bisKm),
      proKm: asText(staffel.proKm),
      pauschale: asText(staffel.pauschale)
    })),
    wartezeit: {
      freiMinuten: asText(data.positionen?.wartezeit?.freiMinuten),
      proStunde: asText(data.positionen?.wartezeit?.proStunde)
    },
    standzeit: {
      freiMinuten: asText(data.positionen?.standzeit?.freiMinuten),
      proStunde: asText(data.positionen?.standzeit?.proStunde)
    },
    zuschlaege: {
      rollstuhl: asText(data.positionen?.zuschlaege?.rollstuhl),
      tragestuhl: asText(data.positionen?.zuschlaege?.tragestuhl),
      treppenhilfeProEtage: asText(data.positionen?.zuschlaege?.treppenhilfeProEtage),
      begleitperson: asText(data.positionen?.zuschlaege?.begleitperson)
    },
    mindestpreis: asText(data.positionen?.mindestpreis),
    sammelfahrtRegel: asText(data.positionen?.sammelfahrtRegel) || 'none'
  }
});

const isExpired = (data) => {
  const until = data.gueltigBis?.toDate ? data.gueltigBis.toDate() : null;
  return !!until && until.getTime() < Date.now();
};

const MedicalTariffsTab = () => {
  const currentUser = useSelector(state => state.auth.user);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { id | null, form }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, COLLECTION),
      (snapshot) => {
        setTariffs(snapshot.docs.map(entry => ({ id: entry.id, ...entry.data() })));
        setLoading(false);
      },
      (snapshotError) => {
        console.error('Tarife konnten nicht geladen werden:', snapshotError);
        setError('Tarife konnten nicht geladen werden.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const startCreate = () => setEditing({ id: null, form: JSON.parse(JSON.stringify(EMPTY_TARIFF)) });

  const startEdit = (tariff) => setEditing({ id: tariff.id, form: fromFirestoreTariff(tariff) });

  /** Häufigster Vorgang: Vertrag ins neue Jahr fortschreiben — meist ändern
   *  sich nur die Beträge, nicht die Struktur. */
  const startDuplicate = (tariff) => {
    const form = fromFirestoreTariff(tariff);
    form.bezeichnung = `${form.bezeichnung} (Kopie)`;
    form.gueltigVon = '';
    form.gueltigBis = '';
    form.istStandard = false;
    setEditing({ id: null, form });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...toFirestoreTariff(editing.form),
        gepflegtVon: currentUser?.uid || null,
        gepflegtAm: serverTimestamp()
      };
      if (editing.id) {
        await setDoc(doc(db, COLLECTION, editing.id), payload, { merge: true });
      } else {
        await addDoc(collection(db, COLLECTION), payload);
      }
      setEditing(null);
    } catch (saveError) {
      console.error('Tarif konnte nicht gespeichert werden:', saveError);
      setError(saveError.message || 'Tarif konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tariff) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Vertrag „${tariff.bezeichnung || tariff.tarifkennzeichen}" wirklich löschen?`)) return;
    try {
      await deleteDoc(doc(db, COLLECTION, tariff.id));
    } catch (deleteError) {
      console.error('Tarif konnte nicht gelöscht werden:', deleteError);
      setError(deleteError.message || 'Tarif konnte nicht gelöscht werden.');
    }
  };

  if (loading) {
    return <Alert severity="info">Tarife werden geladen…</Alert>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {tariffs.length === 0 ? (
        <EmptyState onCreate={startCreate} />
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Diese Werte dienen der Kalkulation. <strong>Maßgeblich ist Ihr
            Kassenvertrag</strong> und die Bewertung durch Ihr
            Abrechnungszentrum. Halten Sie die Beträge aktuell — die
            Vergütungsvereinbarungen ändern sich in der Regel jährlich.
          </Alert>

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>
              Vertrag anlegen
            </Button>
          </Stack>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bezeichnung</TableCell>
                  <TableCell>Kennzeichen</TableCell>
                  <TableCell>Verkehrsform</TableCell>
                  <TableCell>Gültig</TableCell>
                  <TableCell>Kostenträger</TableCell>
                  <TableCell align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tariffs.map(tariff => (
                  <TableRow key={tariff.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{tariff.bezeichnung || '–'}</Typography>
                        {tariff.istStandard && <Chip size="small" label="Standard" />}
                        {tariff.aktiv === false && <Chip size="small" color="default" variant="outlined" label="Inaktiv" />}
                        {isExpired(tariff) && <Chip size="small" color="warning" label="Abgelaufen" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{tariff.tarifkennzeichen || '–'}</TableCell>
                    <TableCell>{tariff.verkehrsform === 'mietwagen' ? 'Mietwagen' : 'Taxi'}</TableCell>
                    <TableCell>
                      {toDateInput(tariff.gueltigVon) || '–'}
                      {' – '}
                      {toDateInput(tariff.gueltigBis) || 'offen'}
                    </TableCell>
                    <TableCell>
                      {Array.isArray(tariff.kostentraegerIks) && tariff.kostentraegerIks.length > 0
                        ? `${tariff.kostentraegerIks.length} IK`
                        : '–'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => startEdit(tariff)} title="Bearbeiten">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => startDuplicate(tariff)} title="Fortschreiben">
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(tariff)} title="Löschen">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {editing && (
        <TariffDialog
          editing={editing}
          setEditing={setEditing}
          onSave={save}
          saving={saving}
        />
      )}
    </Box>
  );
};

/** §8.6 — sagt im ersten Satz, dass nichts eingetragen werden muss. */
const EmptyState = ({ onCreate }) => (
  <Paper sx={{ p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Vergütungsvereinbarungen hinterlegen — optional
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Sie müssen hier nichts eintragen. Krankenfahrten werden verbindlich von
      Ihrem Abrechnungszentrum (DMRZ / opta data) anhand der dort hinterlegten
      Kassenverträge bewertet — der Export funktioniert auch ohne diese Seite.
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
      Wenn Sie Ihre Verträge trotzdem hinterlegen, bekommen Sie zusätzlich:
    </Typography>
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
      <li><Typography variant="body2">eine <strong>Erlösvorschau</strong> für Disposition und Fahrer</Typography></li>
      <li><Typography variant="body2">einen <strong>berechneten Zuzahlungsbetrag</strong> statt nur „pflichtig/befreit"</Typography></li>
      <li><Typography variant="body2">eine <strong>Kontrolle</strong>, ob die Abrechnung dem Vertrag entspricht</Typography></li>
      <li><Typography variant="body2"><strong>realistische Umsatzzahlen</strong>, bevor die Kasse zahlt</Typography></li>
    </Box>
    <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
      Ersten Vertrag anlegen
    </Button>
  </Paper>
);

const TariffDialog = ({ editing, setEditing, onSave, saving }) => {
  const { form } = editing;

  const setForm = useCallback((updater) => {
    setEditing(previous => ({ ...previous, form: updater(previous.form) }));
  }, [setEditing]);

  const setField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const setPosition = (path) => (event) => {
    const value = event.target.value;
    setForm(previous => {
      const next = { ...previous, positionen: { ...previous.positionen } };
      if (path.length === 1) {
        next.positionen[path[0]] = value;
      } else {
        next.positionen[path[0]] = { ...next.positionen[path[0]], [path[1]]: value };
      }
      return next;
    });
  };

  const setStaffel = (index, field) => (event) => {
    const value = event.target.value;
    setForm(previous => {
      const staffeln = previous.positionen.kmStaffeln.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return { ...previous, positionen: { ...previous.positionen, kmStaffeln: staffeln } };
    });
  };

  const addStaffel = () => setForm(previous => ({
    ...previous,
    positionen: {
      ...previous.positionen,
      kmStaffeln: [...previous.positionen.kmStaffeln, { vonKm: '', bisKm: '', proKm: '', pauschale: '' }]
    }
  }));

  const removeStaffel = (index) => setForm(previous => ({
    ...previous,
    positionen: {
      ...previous.positionen,
      kmStaffeln: previous.positionen.kmStaffeln.filter((_, entryIndex) => entryIndex !== index)
    }
  }));

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => setEditing(null)}>
      <DialogTitle>{editing.id ? 'Vertrag bearbeiten' : 'Vertrag anlegen'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField label="Bezeichnung" value={form.bezeichnung} onChange={setField('bezeichnung')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Tarifkennzeichen" value={form.tarifkennzeichen} onChange={setField('tarifkennzeichen')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="verkehrsform-label">Verkehrsform</InputLabel>
              <Select labelId="verkehrsform-label" label="Verkehrsform" value={form.verkehrsform} onChange={setField('verkehrsform')}>
                <MenuItem value="taxi">Taxi</MenuItem>
                <MenuItem value="mietwagen">Mietwagen</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Gültig von" type="date" value={form.gueltigVon} onChange={setField('gueltigVon')} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Gültig bis" type="date" value={form.gueltigBis} onChange={setField('gueltigBis')} slotProps={{ inputLabel: { shrink: true } }} helperText="Leer = unbefristet" fullWidth />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Kostenträgerkennungen (IK)"
              value={form.kostentraegerIks}
              onChange={setField('kostentraegerIks')}
              helperText="Mehrere durch Komma trennen. Über das IK der Kasse wird der Vertrag der Fahrt zugeordnet."
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={<Switch checked={form.istStandard} onChange={setField('istStandard')} />}
              label="Als Standardvertrag verwenden"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={<Switch checked={form.aktiv} onChange={setField('aktiv')} />}
              label="Aktiv"
            />
          </Grid>

          <Grid size={{ xs: 12 }}><Divider sx={{ my: 1 }}><Chip label="Positionen" size="small" /></Divider></Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Grundpauschale (€)" value={form.positionen.grundpauschale} onChange={setPosition(['grundpauschale'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Mindestpreis (€)" value={form.positionen.mindestpreis} onChange={setPosition(['mindestpreis'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="sammelfahrt-label">Sammelfahrt</InputLabel>
              <Select labelId="sammelfahrt-label" label="Sammelfahrt" value={form.positionen.sammelfahrtRegel} onChange={setPosition(['sammelfahrtRegel'])}>
                <MenuItem value="none">Keine Regel hinterlegt</MenuItem>
                <MenuItem value="split_equal">Fahrpreis gleichmäßig aufteilen</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Kilometer-Staffeln</Typography>
            <Typography variant="caption" color="text.secondary">
              Je Staffel entweder eine Pauschale (fällt einmal an) oder einen Preis je Besetzt-km.
            </Typography>
          </Grid>
          {form.positionen.kmStaffeln.map((staffel, index) => (
            <Grid size={{ xs: 12 }} key={index}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField label="von km" value={staffel.vonKm} onChange={setStaffel(index, 'vonKm')} size="small" sx={{ width: 100 }} />
                <TextField label="bis km" value={staffel.bisKm} onChange={setStaffel(index, 'bisKm')} size="small" sx={{ width: 100 }} placeholder="offen" />
                <TextField label="Pauschale (€)" value={staffel.pauschale} onChange={setStaffel(index, 'pauschale')} size="small" sx={{ width: 140 }} />
                <TextField label="je km (€)" value={staffel.proKm} onChange={setStaffel(index, 'proKm')} size="small" sx={{ width: 140 }} />
                <IconButton size="small" onClick={() => removeStaffel(index)} disabled={form.positionen.kmStaffeln.length === 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>
          ))}
          <Grid size={{ xs: 12 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={addStaffel}>Staffel hinzufügen</Button>
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Wartezeit frei (min)" value={form.positionen.wartezeit.freiMinuten} onChange={setPosition(['wartezeit', 'freiMinuten'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Wartezeit je Std. (€)" value={form.positionen.wartezeit.proStunde} onChange={setPosition(['wartezeit', 'proStunde'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Standzeit frei (min)" value={form.positionen.standzeit.freiMinuten} onChange={setPosition(['standzeit', 'freiMinuten'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Standzeit je Std. (€)" value={form.positionen.standzeit.proStunde} onChange={setPosition(['standzeit', 'proStunde'])} fullWidth />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Rollstuhl (€)" value={form.positionen.zuschlaege.rollstuhl} onChange={setPosition(['zuschlaege', 'rollstuhl'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Tragestuhl (€)" value={form.positionen.zuschlaege.tragestuhl} onChange={setPosition(['zuschlaege', 'tragestuhl'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Treppenhilfe je Etage (€)" value={form.positionen.zuschlaege.treppenhilfeProEtage} onChange={setPosition(['zuschlaege', 'treppenhilfeProEtage'])} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Begleitperson (€)" value={form.positionen.zuschlaege.begleitperson} onChange={setPosition(['zuschlaege', 'begleitperson'])} fullWidth />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Quelle"
              value={form.quelle}
              onChange={setField('quelle')}
              helperText="Für die Nachvollziehbarkeit, z. B. Vergütungsvereinbarung vdek BW ab 01.01.2026"
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}><Divider sx={{ my: 1 }}><Chip label="Testrechner" size="small" icon={<CalcIcon />} /></Divider></Grid>
          <Grid size={{ xs: 12 }}>
            <TariffTestCalculator positionen={form.positionen} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditing(null)}>Abbrechen</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Speichert…' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Rechnet eine Beispielfahrt durch — gegen die aktuell im Formular stehenden
 * Werte, also auch vor dem Speichern. So prüft der Unternehmer seine Eingabe
 * gegen eine reale Abrechnung, bevor er sich darauf verlässt.
 */
const TariffTestCalculator = ({ positionen }) => {
  const [input, setInput] = useState({
    occupiedKm: '12',
    interTripWaitMinutes: '0',
    standbyMinutes: '0',
    stairAssistFlights: '0',
    additionalPassengers: '0',
    capability: 'none',
    escortPresent: false
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState('');

  const positionsKey = useMemo(() => JSON.stringify(positionen), [positionen]);

  const setValue = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setInput(previous => ({ ...previous, [field]: value }));
  };

  const run = async () => {
    setCalculating(true);
    setCalcError('');
    try {
      const response = await calculateMedicalTariffPreview({
        positionen: toFirestorePositions(JSON.parse(positionsKey)),
        fahrt: {
          occupiedKm: toNumberOrNull(input.occupiedKm) ?? 0,
          interTripWaitMinutes: toNumberOrNull(input.interTripWaitMinutes),
          standbyMinutes: toNumberOrNull(input.standbyMinutes),
          stairAssistFlights: toNumberOrNull(input.stairAssistFlights),
          additionalPassengers: toNumberOrNull(input.additionalPassengers),
          capabilities: input.capability === 'none' ? [] : [input.capability],
          escortPresent: input.escortPresent === true
        }
      });
      setResult(response);
    } catch (previewError) {
      console.error('Testrechnung fehlgeschlagen:', previewError);
      setCalcError(previewError.message || 'Testrechnung fehlgeschlagen.');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={1} alignItems="center">
        <Grid size={{ xs: 6, sm: 2 }}>
          <TextField label="Besetzt-km" value={input.occupiedKm} onChange={setValue('occupiedKm')} size="small" fullWidth />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <TextField label="Wartezeit (min)" value={input.interTripWaitMinutes} onChange={setValue('interTripWaitMinutes')} size="small" fullWidth />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <TextField label="Standzeit (min)" value={input.standbyMinutes} onChange={setValue('standbyMinutes')} size="small" fullWidth />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <TextField label="Etagen" value={input.stairAssistFlights} onChange={setValue('stairAssistFlights')} size="small" fullWidth />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="test-capability-label">Merkmal</InputLabel>
            <Select labelId="test-capability-label" label="Merkmal" value={input.capability} onChange={setValue('capability')}>
              <MenuItem value="none">keins</MenuItem>
              <MenuItem value="rollstuhl">Rollstuhl</MenuItem>
              <MenuItem value="tragestuhl">Tragestuhl</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <Button variant="outlined" onClick={run} disabled={calculating} fullWidth>
            {calculating ? '…' : 'Rechnen'}
          </Button>
        </Grid>
      </Grid>

      {calcError && <Alert severity="error" sx={{ mt: 1 }}>{calcError}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Stack spacing={0.5}>
            {result.positions?.map((position, index) => (
              <Stack key={index} direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{position.label}</Typography>
                <Typography variant="body2">{formatEur(position.amountEur)}</Typography>
              </Stack>
            ))}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Summe (Schätzung)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatEur(result.totalEur)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">davon Zuzahlung (§ 61 SGB V)</Typography>
              <Typography variant="body2">{formatEur(result.copaymentEur)}</Typography>
            </Stack>
          </Stack>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Unverbindlich</AlertTitle>
            Diese Rechnung zeigt, was Ihr hinterlegter Vertrag ergibt.
            Verbindlich bewertet Ihr Abrechnungszentrum.
          </Alert>
        </Paper>
      )}
    </Box>
  );
};

export default MedicalTariffsTab;
